/**
 * Validación Zod del formulario de precios del cotizador.
 *
 * Se valida ANTES de enviar (`docs/frontend_conventions.md`, Validación) porque el
 * backend de `guardar_precios` NO valida el payload: ante un `NaN`/vacío hace
 * `float(...)` y revienta con 500 (`progress/explore_precios_api.md` §4). Por eso
 * aquí se exige, como mínimo, que los numéricos sean **finitos** y no negativos.
 *
 * `iva` y `factor_utilidad` son **multiplicadores/factores** (1.19, 0.9), no
 * porcentajes: se exigen `> 0` (un factor de 0 no tiene sentido y rompería el
 * cálculo). Los precios escalares se exigen `>= 0` (el SSR acepta 0).
 *
 * El filtrado de filas no positivas en las estructuras dinámicas (calibres,
 * combinadas, tablas) es una **transformación** (en `precios.transform.ts`), no un
 * error de validación: aquí solo se comprueba que las celdas con texto sean
 * numéricas finitas, para no mandar `NaN` dentro de una tabla.
 */
import { z } from "zod";

import type { PrecioEscalarKey } from "./precios.constants";
import type { PreciosFormState } from "./precios.transform";

/** Error de validación por campo escalar (clave -> mensaje). */
export type PreciosFieldErrors = Partial<Record<PrecioEscalarKey, string>>;

/**
 * Numérico requerido `>= 0` a partir de un string de input (vacío = error). Se
 * parsea con `Number` para distinguir vacío/no numérico de un 0 legítimo.
 */
function numericNonNegative(message: string) {
  return z
    .string()
    .trim()
    .refine((raw) => raw !== "", "Este campo es obligatorio.")
    .refine((raw) => Number.isFinite(Number(raw)), "Introduce un número válido.")
    .refine((raw) => Number(raw) >= 0, message);
}

/** Numérico requerido estrictamente `> 0` (para factores como iva/utilidad). */
function numericPositive(message: string) {
  return z
    .string()
    .trim()
    .refine((raw) => raw !== "", "Este campo es obligatorio.")
    .refine((raw) => Number.isFinite(Number(raw)), "Introduce un número válido.")
    .refine((raw) => Number(raw) > 0, message);
}

/**
 * Esquema de los 10 escalares (la entrada es el `escalares` del estado del form:
 * un `Record<clave, string>`). Mensajes en español, anclados por campo.
 */
export const preciosEscalaresSchema = z.object({
  precio_kg_venta: numericNonNegative("El precio no puede ser negativo."),
  costo_kg_compra: numericNonNegative("El costo no puede ser negativo."),
  factor_utilidad: numericPositive("El factor de utilidad debe ser mayor que 0."),
  iva: numericPositive("El IVA debe ser un multiplicador mayor que 0 (ej. 1.19)."),
  perfiles: numericNonNegative("El valor no puede ser negativo."),
  cantidad: numericNonNegative("El valor no puede ser negativo."),
  dobles_interno_general: numericNonNegative("El valor no puede ser negativo."),
  dobles_refuerzo: numericNonNegative("El valor no puede ser negativo."),
  dobles_por_tubo: numericNonNegative("El valor no puede ser negativo."),
  dobles_division: numericNonNegative("El valor no puede ser negativo."),
});

/**
 * Comprueba que toda celda *con texto* de las estructuras dinámicas sea numérica
 * finita (las vacías se descartan luego; un texto no numérico sí es error: el
 * usuario tecleó algo inválido). Devuelve un mensaje general o `null` si todo OK.
 */
export function validateDynamicCells(state: PreciosFormState): string | null {
  const isInvalid = (raw: string): boolean => {
    const trimmed = raw.trim();
    return trimmed !== "" && !Number.isFinite(Number(trimmed));
  };

  for (const row of state.dobles) {
    if (isInvalid(row.valor)) {
      return "Hay un precio de doblez por calibre que no es un número válido.";
    }
  }
  for (const row of state.combinada) {
    if (isInvalid(row.ancho) || isInvalid(row.alto)) {
      return "Hay una medida combinada con un valor que no es un número válido.";
    }
  }
  for (const tabla of Object.values(state.tablasCalibre)) {
    for (const rows of Object.values(tabla)) {
      for (const row of rows) {
        if (isInvalid(row.hasta) || isInvalid(row.precio)) {
          return "Hay una tabla por medida con un valor que no es un número válido.";
        }
      }
    }
  }
  for (const row of state.tuboRemarco) {
    if (isInvalid(row.hasta) || isInvalid(row.precio)) {
      return "Hay una tabla por medida con un valor que no es un número válido.";
    }
  }
  return null;
}

/**
 * Valida todo el formulario. Devuelve los errores por campo escalar y, aparte, un
 * posible error general de las celdas dinámicas. Si no hay errores, ambos vacíos.
 */
export function validatePreciosForm(state: PreciosFormState): {
  fieldErrors: PreciosFieldErrors;
  formError: string | null;
} {
  const result = preciosEscalaresSchema.safeParse(state.escalares);
  const fieldErrors: PreciosFieldErrors = {};
  if (!result.success) {
    const flattened = result.error.flatten().fieldErrors;
    for (const [key, messages] of Object.entries(flattened)) {
      const first = messages?.[0];
      if (first) {
        fieldErrors[key as PrecioEscalarKey] = first;
      }
    }
  }
  const formError = validateDynamicCells(state);
  return { fieldErrors, formError };
}
